import { ExternalLink, ShieldCheck } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal, Overline } from "./Reveal";

const AuthTable = ({ title, rows, t, testId }) => (
  <div data-testid={testId} className="border border-slate-700/60 bg-slate-800/40">
    <div className="px-6 py-4 border-b border-slate-700/60">
      <h4 className="text-sm font-medium text-white">{title}</h4>
    </div>
    <div className="grid grid-cols-[2fr_0.7fr_0.7fr] px-6 py-3 border-b border-slate-700/40">
      {[t.regulation.colService, t.regulation.colStart, t.regulation.colEnd].map((h) => (
        <span key={h} className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{h}</span>
      ))}
    </div>
    {rows.map((r, i) => (
      <div key={i} className="grid grid-cols-[2fr_0.7fr_0.7fr] px-6 py-4 border-b border-slate-700/30 last:border-0">
        <span className="text-sm text-slate-300 pr-4">{r}</span>
        <span className="text-sm font-mono text-slate-400">20/10/2014</span>
        <span className="text-sm font-mono text-emerald-400">{t.regulation.ongoing}</span>
      </div>
    ))}
  </div>
);

export const RegulatorySection = () => {
  const { t } = useLang();
  return (
    <section id="regulation" data-testid="regulation-section" className="bg-slate-900 py-28 lg:py-36 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "64px 64px" }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="eyebrow inline-flex items-center gap-3 text-xs uppercase tracking-[0.28em] font-mono font-medium text-emerald-400">
            <span className="h-px w-8 bg-emerald-500" />
            {t.regulation.overline}
          </span>
          <h2 className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">
            {t.regulation.title}
          </h2>
          <p className="mt-5 text-base lg:text-lg text-slate-400 leading-relaxed max-w-2xl">{t.regulation.lead}</p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start">
          <Reveal delay={0.1}>
            <div data-testid="cssf-register-card" className="border border-emerald-600/40 bg-slate-800/60 backdrop-blur-sm">
              <div className="px-6 py-5 border-b border-slate-700/60 flex items-center gap-3">
                <ShieldCheck size={18} className="text-emerald-400" />
                <h3 className="text-base font-medium text-white">{t.regulation.cardTitle}</h3>
              </div>
              {t.regulation.fields.map(([k, v]) => (
                <div key={k} className="px-6 py-4 border-b border-slate-700/30 last:border-0 flex flex-col gap-1">
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{k}</span>
                  <span className="text-sm text-slate-200">{v}</span>
                </div>
              ))}
              <div className="px-6 py-5">
                <a
                  data-testid="cssf-verify-link"
                  href="https://edesk.apps.cssf.lu/search-entities/entite/details/7013460?lng=en&q=&st=advanced&entNames=asteo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors duration-300"
                >
                  {t.regulation.verify} <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </Reveal>

          <div className="space-y-8">
            <Reveal delay={0.15}>
              <AuthTable title={t.regulation.servicesTitle} rows={t.regulation.services} t={t} testId="regulation-services-table" />
            </Reveal>
            <Reveal delay={0.2}>
              <AuthTable title={t.regulation.auxTitle} rows={t.regulation.aux} t={t} testId="regulation-aux-table" />
            </Reveal>
            <Reveal delay={0.25}>
              <AuthTable title={t.regulation.statusTitle} rows={t.regulation.status} t={t} testId="regulation-status-table" />
              <p className="mt-6 text-xs font-mono text-slate-500">{t.regulation.history}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
